// Passkeys, on the Security page (round 32).
//
// A peer of the authenticator app, not a replacement — the copy says so,
// because the first question anyone asks is "does this mean I stop typing my
// password?" and the answer is no.
//
// THE ONE PIECE OF REAL BROWSER WORK IN THE APP. `startRegistration` and
// `startAuthentication` wrap `navigator.credentials`, which throws for half a
// dozen ordinary reasons — the person cancelled the system prompt, the device
// has no authenticator, the passkey is already registered, the page is not on
// a secure origin. None of those are errors in the Dayboard sense and none
// should be shown as one, so they are turned into sentences below rather than
// surfaced as whatever the browser called them.
import { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { ApiError, api, type SecurityStatus } from '../../api';
import { Banner, Field } from '../../ui';

/** What the browser threw, as something a person can act on. */
function ceremonyMessage(err: unknown): string | null {
  const name = err instanceof Error ? err.name : '';
  // The person closed the prompt. Not a failure; say nothing at all.
  if (name === 'NotAllowedError' || name === 'AbortError') return null;
  if (name === 'InvalidStateError') {
    return 'This device already has a passkey for Dayboard. It is in the list above.';
  }
  if (name === 'NotSupportedError') {
    return 'This device cannot make a passkey. Your authenticator app still works.';
  }
  if (name === 'SecurityError') {
    return 'Passkeys need a secure connection. Open Dayboard at its usual address and try again.';
  }
  return 'That did not work. Try again, or use your authenticator app.';
}

export function PasskeysPanel({
  status,
  onChanged,
}: {
  status: SecurityStatus;
  onChanged: (message: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  function reset() {
    setAdding(false);
    setRemoving(null);
    setPassword('');
    setTotpCode('');
    setName('');
    setFields({});
    setError(null);
  }

  /** The password, and a code only when the account has an app to give one. */
  function reauth() {
    return {
      currentPassword: password,
      ...(status.totpEnabled && totpCode !== '' ? { totpCode } : {}),
    };
  }

  function handle(err: unknown, fallback: string) {
    if (err instanceof ApiError) {
      setFields(err.fields ?? {});
      setError(err.fields ? null : err.message);
    } else {
      setError(fallback);
    }
  }

  async function add(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      // Two round trips, because the ceremony is two round trips: the server
      // issues a challenge, the authenticator signs it. The password is asked
      // for on the FIRST, so nothing is half-done if it is wrong.
      const started = await api.passkeyOptions(reauth());
      const response = await startRegistration({ optionsJSON: started.options });
      const saved = await api.passkeyAdd({
        token: started.token,
        response,
        ...(name.trim() !== '' ? { name: name.trim() } : {}),
      });
      reset();
      onChanged(`“${saved.passkey.name}” can now sign you in.`);
    } catch (err) {
      if (err instanceof ApiError) {
        handle(err, 'Could not add that passkey.');
      } else {
        const message = ceremonyMessage(err);
        if (message) setError(message);
        else reset();
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove(event: React.FormEvent) {
    event.preventDefault();
    if (!removing) return;
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.passkeyRemove(removing, reauth());
      reset();
      onChanged('That passkey has been removed.');
    } catch (err) {
      handle(err, 'Could not remove that passkey.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">Passkeys</h2>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      <p>
        A passkey is your phone or laptop unlocking Dayboard with a fingerprint, a face or a
        PIN, instead of typing a code. You still enter your password first; a passkey sits
        beside your authenticator app, not in place of it.
      </p>

      {status.passkeys.length === 0 ? (
        <p className="muted">No passkeys yet.</p>
      ) : (
        <ul className="space-y-2">
          {status.passkeys.map((passkey) => (
            <li key={passkey.id} className="rounded border border-gray-200 p-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium text-gray-900">{passkey.name}</span>
                {passkey.backedUp && <span className="pill">Synced</span>}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Added {new Date(passkey.createdAt).toLocaleDateString('en-AU')}
                {passkey.lastUsedAt
                  ? ` · last used ${new Date(passkey.lastUsedAt).toLocaleDateString('en-AU')}`
                  : ' · never used'}
              </div>
              {removing === passkey.id ? (
                <form onSubmit={remove} className="mt-3">
                  <ReauthPair
                    idPrefix={`remove-${passkey.id}`}
                    password={password}
                    setPassword={setPassword}
                    code={totpCode}
                    setCode={setTotpCode}
                    totpEnabled={status.totpEnabled}
                    fields={fields}
                  />
                  <div className="row">
                    <button type="submit" className="btn-danger" disabled={busy}>
                      Remove it
                    </button>
                    <button type="button" className="btn-secondary" onClick={reset}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="row-action-danger mt-1"
                  onClick={() => {
                    reset();
                    setRemoving(passkey.id);
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <form onSubmit={add} className="mt-4">
          <ReauthPair
            idPrefix="add-passkey"
            password={password}
            setPassword={setPassword}
            code={totpCode}
            setCode={setTotpCode}
            totpEnabled={status.totpEnabled}
            fields={fields}
          />
          <Field
            label="Name it (optional)"
            name="passkey-name"
            value={name}
            onChange={setName}
            error={fields.passkey}
          />
          <p className="hint">
            Something you will recognise in a list: “work phone”, “ute laptop”. Left blank,
            Dayboard names it after the device you are on.
          </p>
          <div className="row">
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Waiting for your device…' : 'Add this device'}
            </button>
            <button type="button" className="btn-secondary" onClick={reset}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn-secondary mt-3"
          onClick={() => {
            reset();
            setAdding(true);
          }}
        >
          Add a passkey
        </button>
      )}
    </section>
  );
}

/**
 * The password-and-code pair, again.
 *
 * A local copy rather than an import: `ReauthFields` lives inside
 * SecurityPage.tsx and is not exported, and exporting it to share with one
 * caller would make that page's private helper part of a public surface. Two
 * short forms, one rule — if a third appears, that is the moment to move it.
 */
function ReauthPair({
  idPrefix,
  password,
  setPassword,
  code,
  setCode,
  totpEnabled,
  fields,
}: {
  idPrefix: string;
  password: string;
  setPassword: (value: string) => void;
  code: string;
  setCode: (value: string) => void;
  totpEnabled: boolean;
  fields: Record<string, string>;
}) {
  return (
    <>
      <Field
        label="Your current password"
        name={`${idPrefix}-currentPassword`}
        type="password"
        value={password}
        onChange={setPassword}
        error={fields.currentPassword}
        autoComplete="current-password"
      />
      {totpEnabled && (
        <Field
          label="Code from your authenticator app"
          name={`${idPrefix}-totpCode`}
          value={code}
          onChange={setCode}
          error={fields.totpCode}
          autoComplete="one-time-code"
          inputMode="numeric"
          autoCapitalize="none"
          spellCheck={false}
        />
      )}
    </>
  );
}
